import React, { useEffect, useMemo, useState } from 'react';
import './ChatHeader.css';
import MobileCarSearch from './MobileCarSearch';
import RemoveIcon from '@mui/icons-material/Remove';
import { createTranslator, getBrowserLanguage } from '../../utils/i18n';

const ChatHeader = ({
  isMobile,
  toggleChat,
  toggleChatWidth,
  isChatWide,
  isLoading,
  onSendMessage,
  isHumanControlled,
  humanControlMessage,
  controller,
  isOnline,
  toggleSearchPanel,
  onCarSearchOpen,
  onCarSearchClose,
  isCompactMode,
  toggleCompactMode,
  openedByTrigger,
  onWaitingStateChange,
  isConnected,
  isAuthenticated,
  statusReceived,
}) => {
  const [dealerLogo, setDealerLogo] = useState(null);
  const [dealerName, setDealerName] = useState('');
  const [showCarSearch, setShowCarSearch] = useState(false);
  const [showTransition, setShowTransition] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const language = useMemo(() => getBrowserLanguage(), []);
  const t = useMemo(() => createTranslator(language), [language]);
  
  // Add state to control when to show human control-dependent elements
  const [showHumanControlElements, setShowHumanControlElements] = useState(false);
  
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Show human control elements when WebSocket status is received
  useEffect(() => {
    if (isConnected && isAuthenticated && statusReceived) {
      console.log('✅ ChatHeader: WebSocket status received - showing human control elements');
      setShowHumanControlElements(true);
    } else {
      console.log('⏳ ChatHeader: WebSocket status not ready, hiding human control elements', {
        isConnected,
        isAuthenticated,
        statusReceived
      });
      setShowHumanControlElements(false);
    }
  }, [isConnected, isAuthenticated, statusReceived]);

  const handleCloseChat = () => {
    handleMenuClose();
    // On mobile, add a better delay and set document overflow to avoid flickering
    if (isMobile) {
      // Immediately fix body overflow to prevent layout shifts
      document.body.style.overflow = 'auto';
      
      // Add a delay before actually toggling the chat
      setTimeout(() => {
        toggleChat();
      }, 100);
    } else {
      toggleChat();
    }
  };



  useEffect(() => {
    // Get dealer logo and name from localStorage
    const logo = localStorage.getItem("logo");
    const name = localStorage.getItem("name_chat");
    
    if (logo) setDealerLogo(logo);
    if (name) setDealerName(name);
  }, []);

  // Add animation effect when human control changes
  useEffect(() => {
    setShowTransition(true);
    const timer = setTimeout(() => {
      setShowTransition(false);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [isHumanControlled]);

  // Notify parent when car search panel visibility changes
  useEffect(() => {
    if (onCarSearchOpen && showCarSearch) {
      onCarSearchOpen();
    } else if (onCarSearchClose && !showCarSearch) {
      onCarSearchClose();
    }
  }, [showCarSearch, onCarSearchOpen, onCarSearchClose]);

  // Notify parent when waiting state changes
  useEffect(() => {
    if (onWaitingStateChange) {
      const isWaitingForOperator = isHumanControlled && (!controller || !controller.first_name);
      onWaitingStateChange(isWaitingForOperator);
    }
  }, [isHumanControlled, controller, onWaitingStateChange]);

  const handleCarSearchSubmit = (searchData) => {
    setShowCarSearch(false);
    
    // Format the search data into a message
    const message = `Sto cercando un'auto con queste caratteristiche:
- Budget: ${searchData.budget}€
- Percorrenza annuale: ${searchData.annualKm} km
- Alimentazione preferita: ${searchData.fuelType}
${searchData.additionalInfo ? `- Note aggiuntive: ${searchData.additionalInfo}` : ''}`;
    
    // Send the message
    if (onSendMessage) {
      onSendMessage(message);
    }
  };

  return (
    <>
      <div className={`mia-chat-header 
        ${showHumanControlElements && isHumanControlled ? 'mia-human-operator-active' : ''} 
        ${showTransition ? 'mia-transitioning' : ''}
        ${(isCompactMode || openedByTrigger) && isMobile ? 'mia-compact-mode' : ''}
      `}>
        <div className="mia-chat-header-title">
          <div className="mia-profile-image">
            {dealerLogo ? (
              <img src={dealerLogo} alt="Dealer Logo" />
            ) : (
              <img src="/assets/images/bot-avatar.png" alt={t('aiAssistant')} />
            )}
            <span className={`mia-status-indicator ${isOnline ? (showHumanControlElements && isHumanControlled ? (controller && controller.first_name ? 'mia-human-operator' : 'mia-human-waiting') : 'mia-online') : 'mia-offline'}`}></span>
          </div>
          <div className="mia-title-container">
            <div className="mia-name-and-badge">
              <div className="mia-name-wrapper">
                <span className="mia-chat-bot-name">{dealerName || t('assistantAuto')}</span>
              </div>
              <div className="mia-status-wrapper">
                {!showHumanControlElements ? (
                  <div className="mia-status-container mia-ai-active">
                    <div className="mia-status-icon">
                      <img 
                        src="https://files.aitomotivelab.com/Mia_Logo_Black.png" 
                        alt="MIA"
                        width="16"
                        height="16"
                      />
                    </div>
                    <div className="mia-status-text">
                      <span className="mia-status-label">{t('assistant')}</span>
                      <span className="mia-status-sublabel">MIA</span>
                    </div>
                  </div>
                ) : isHumanControlled ? (
                  controller && controller.first_name ? (
                    <div className="mia-status-container mia-operator-active">
                      <div className="mia-status-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                          <circle cx="12" cy="7" r="4"></circle>
                        </svg>
                      </div>
                      <div className="mia-status-text">
                        <span className="mia-status-label">{t('operator')}</span>
                        <span className="mia-status-name">{controller.first_name}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="mia-status-container mia-operator-waiting">
                      <div className="mia-status-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="3"></circle>
                          <path d="M12 1v6m0 6v6m11-7h-6m-6 0H1"></path>
                        </svg>
                      </div>
                      <div className="mia-status-text">
                        <span className="mia-status-label">{t('waiting')}</span>
                        <span className="mia-status-sublabel">{t('operatorLower')}</span>
                      </div>
                    </div>
                  )
                ) : (
                  <div className="mia-status-container mia-ai-active">
                    <div className="mia-status-icon">
                      <img 
                        src="https://files.aitomotivelab.com/Mia_Logo_Black.png" 
                        alt="MIA"
                        width="16"
                        height="16"
                      />
                    </div>
                    <div className="mia-status-text">
                      <span className="mia-status-label">{t('assistant')}</span>
                      <span className="mia-status-sublabel">MIA</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          

        </div>
        

        
        <div className="mia-chat-header-actions">
          {isMobile && (
            <button
              className="mia-header-button mia-close-button"
              onClick={handleCloseChat}
              aria-label={t('closeChat')}
              title={t('closeChat')}
            >
              <RemoveIcon style={{ width: 24, height: 24 }} />
            </button>
          )}
        </div>
      </div>
      
      {showCarSearch && (
        <MobileCarSearch 
          onClose={() => setShowCarSearch(false)} 
          onSubmit={handleCarSearchSubmit}
        />
      )}
    </>
  );
};

export default ChatHeader; 
